/**
 * <WelcomeBackBox /> — Session 6 offline claim modal.
 *
 * Shown when the character document carries a `pending_claim` snapshot.
 * The gains are ALREADY on the character (server-authoritative regen), so
 * this modal is purely a "you were away for X hours; here's what you missed"
 * acknowledgement. Clicking CLAIM POSTs the nonce and clears the box.
 *
 * Idempotency guarantees:
 * - The nonce protects against double-submit (server returns already_claimed)
 * - Multiple tabs all show the box until whoever clicks first wins the atomic
 *   $unset. Losing tabs get already_claimed=true and hide the modal too.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Heart, Zap, Compass, Sparkles, ScrollText, X } from "lucide-react";

const RESOURCE_META = {
  hp:             { label: "Health",         Icon: Heart,      color: "#B02020" },
  explore_points: { label: "Explore Points", Icon: Compass,    color: "#E55A00" },
  quest_points:   { label: "Quest Points",   Icon: ScrollText, color: "#E5C34A" },
  mana:           { label: "Mana",           Icon: Sparkles,   color: "#4A7BC7" },
  vigor:          { label: "Vigor",          Icon: Zap,        color: "#B89C3A" },
};

function formatOffline(seconds) {
  const s = Math.max(0, Number(seconds || 0));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  return h > 0 ? `${d}d ${h}h` : `${d}d`;
}


export function WelcomeBackBox({ ch, setCh }) {
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const pending = ch?.pending_claim;
  if (!pending || dismissed) return null;

  const gained = pending.gained || {};
  const rows = Object.entries(gained).filter(([, v]) => Number(v) > 0);
  if (rows.length === 0) return null;   // nothing to claim, quietly hide

  const claim = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await api.claimOffline(pending.nonce);
      // Whether claimed or already_claimed, clear the box on the client.
      setCh(prev => {
        if (!prev) return prev;
        const { pending_claim, ...rest } = prev;
        return rest;
      });
      if (res.claimed) {
        toast.success("Welcome back!");
      }
      // No noisy "already claimed" toast — user just wants the box gone.
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not claim");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
         data-testid="welcome-back-modal">
      <div className="panel max-w-sm w-full p-5 relative fade-up">
        <button onClick={() => setDismissed(true)} data-testid="welcome-back-close"
          className="absolute top-2 right-2 text-[#666] hover:text-[#E2DFD8]">
          <X size={16} strokeWidth={1.6} />
        </button>

        <div className="font-heading text-sm tracking-[0.3em] uppercase text-[#E5C34A] text-center">
          Welcome Back
        </div>
        <div className="font-mono text-[10px] text-[#8c8c8c] tracking-widest uppercase text-center mt-1">
          Away for {formatOffline(pending.offline_seconds)}
        </div>

        <div className="italic text-xs text-[#B0ADA5] text-center mt-3 mb-4">
          The road kept turning while you were gone. Here's what you gathered:
        </div>

        <div className="space-y-1.5">
          {rows.map(([key, val]) => {
            const meta = RESOURCE_META[key] || { label: key, Icon: Sparkles, color: "#B0ADA5" };
            const Icon = meta.Icon;
            return (
              <div key={key}
                data-testid={`welcome-row-${key}`}
                className="flex items-center gap-2 panel-inner px-3 py-2">
                <Icon size={14} strokeWidth={1.8} style={{ color: meta.color }} />
                <span className="font-mono text-xs text-[#B0ADA5] flex-1">{meta.label}</span>
                <span className="font-heading text-sm" style={{ color: meta.color }}>
                  +{val}
                </span>
              </div>
            );
          })}
        </div>

        <button
          data-testid="welcome-back-claim"
          onClick={claim}
          disabled={busy}
          className="mt-5 w-full py-2.5 border border-[#B89C3A] text-[#E5C34A] hover:bg-[#B89C3A]/10 font-heading text-xs tracking-[0.3em] uppercase transition-colors disabled:opacity-30">
          {busy ? "Claiming..." : "Claim"}
        </button>
      </div>
    </div>
  );
}
